import * as React from 'react';
import { Edit, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { EmptyState } from '@/components/ui/empty-state';
import type { FirmUserListItem } from '@/lib/api/settings';

/**
 * Users & roles table (T-0110 / E01-F07).
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-firm.js →
 * window.Screens.firmUsers`. Columns mirror the prototype's `.dtable`
 * markup: User (name + email subline), Role (role pill), sign-in method,
 * last sign-in, status, per-row actions.
 *
 * Sign-in column (T-0227 / C2-20): the prototype drew a per-user 2FA flag,
 * and the table showed "Not enrolled" for everyone. Multi-factor is
 * enforced by Microsoft Entra conditional access, not by Caliber, and the
 * API does not read enrollment state from Entra, so a per-user flag here
 * could only be wrong. The column states the one thing that is true for
 * every user: they sign in through Entra, where MFA is policy.
 *
 * Per-row actions:
 *   - "Edit role" → fires `onEditRole(user)` so the parent can open a
 *     role-edit modal. The parent currently just dispatches a console
 *     warning until T-0110's follow-up role editor lands (the ticket
 *     itself only delivers the read view + invite affordance).
 *   - "Manage overrides" → fires `onManageOverrides(user)` similarly.
 *
 * The table relies on the `<DataGrid>` primitive so headers + cell padding
 * + hover behavior are consistent with the audit + snapshots tables.
 */

export interface UsersTableProps {
  users: ReadonlyArray<FirmUserListItem>;
  onEditRole?: (user: FirmUserListItem) => void;
  onManageOverrides?: (user: FirmUserListItem) => void;
}

const ROLE_LABEL: Record<string, string> = {
  firm_admin: 'Firm admin',
  engagement_lead: 'Engagement lead',
  consultant: 'Consultant',
};

/** The display name of a firm role; shared with the client team roster (T-0227). */
export function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

export function RoleChip({ role }: { role: string }) {
  return (
    <span className="inline-flex h-[22px] items-center rounded-full bg-paper-100 px-2.5 text-meta font-medium text-ink-700">
      {roleLabel(role)}
    </span>
  );
}

function StatusChip({ status }: { status: string }) {
  // Active = healthy posture tint; deactivated = neutral grey. Per the
  // prototype's `firmUsers` renderer.
  if (status === 'active') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-posture-healthy-tint px-2.5 text-meta font-medium text-posture-healthy">
        Active
      </span>
    );
  }
  if (status === 'deactivated') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-posture-unassessed">
        Deactivated
      </span>
    );
  }
  return (
    <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-meta font-medium text-ink-700">
      {status}
    </span>
  );
}

/** The sign-in method, the same for every firm user. */
export const SIGN_IN_METHOD = 'Microsoft Entra (MFA by policy)';

function SignInChip() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-paper-100 px-2 py-0.5 text-meta font-medium text-ink-700"
      title="Multi-factor authentication is enforced by Entra conditional access. Caliber does not read enrollment state."
    >
      <ShieldCheck className="h-3 w-3 text-ink-500" aria-hidden="true" />
      {SIGN_IN_METHOD}
    </span>
  );
}

/**
 * Relative-time label for `last_sign_in_at`. Renders an em-dash when the
 * user has never authenticated (the column is NULL until the first
 * successful token verify).
 */
function formatRelativeSignIn(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return iso;
  const now = Date.now();
  const deltaMs = now - then.getTime();
  if (deltaMs < 0) return then.toISOString().slice(0, 10);
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo ago`;
  const years = Math.floor(months / 12);
  return `${years} yr${years === 1 ? '' : 's'} ago`;
}

export function UsersTable({ users, onEditRole, onManageOverrides }: UsersTableProps) {
  const columns: ReadonlyArray<DataGridColumn<FirmUserListItem>> = [
    {
      key: 'user',
      header: 'User',
      cell: (u) => (
        <div className="flex flex-col">
          <span className="font-medium text-ink-900">{u.name}</span>
          <span className="text-meta text-ink-500">{u.email}</span>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      cell: (u) => <RoleChip role={u.default_role} />,
    },
    {
      key: 'sign_in',
      header: 'Sign-in',
      cell: () => <SignInChip />,
    },
    {
      key: 'last_sign_in',
      header: 'Last sign-in',
      cell: (u) => (
        <span
          className="font-mono text-meta text-ink-500"
          title={u.last_sign_in_at ?? 'Never signed in.'}
        >
          {formatRelativeSignIn(u.last_sign_in_at ?? null)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (u) => <StatusChip status={u.status} />,
    },
    {
      key: 'actions',
      header: '',
      cellClassName: 'text-right',
      headerClassName: 'text-right',
      cell: (u) => (
        <div className="flex justify-end gap-1.5">
          {onEditRole ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onEditRole(u)}
              className="h-7 gap-1 px-2 text-meta"
            >
              <Edit className="h-3 w-3" aria-hidden="true" />
              Edit role
            </Button>
          ) : null}
          {onManageOverrides ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onManageOverrides(u)}
              className="h-7 px-2 text-meta"
            >
              Overrides
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <DataGrid
      columns={columns}
      rows={users}
      rowKey={(u) => u.id}
      caption="Firm users"
      emptyState={
        <EmptyState
          title="No users provisioned"
          description="Invite the first internal user via the 'Invite user' button above."
        />
      }
    />
  );
}
